// 1. Imports
import { useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { 
    Accordion, 
    AccordionDetails, 
    AccordionSummary, 
    Box, 
    Grid,
    Tooltip,
    Typography 
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import FormBuilder from "../../form-builder";
import { MESSAGE_HISTORY_FILTER_INPUTS } from "../../../builders/message-history.builders";
import { GT_COLORS } from "../../../constants/designTokens.constants";

// 2. Component Def
interface Props {
    onFilter(filters: { title: string; body: string }): Promise<void>;
    loading?: boolean;
}

export const MessageHistoryFilters: React.FC<Props> = ({ onFilter, loading = false }) => {
    // 2.1 State and Vars
    const [open, setOpen] = useState(false);

    const formik = useFormik<{ title: string; body: string }>({
        initialValues: { 
            title: '', 
            body: '' 
        },
        onSubmit: async (filters) => {
            await onFilter(filters);
        }
    });

    const {
        errors,
        touched,
        values,
        setFieldValue,
        handleSubmit,
        getFieldProps,
    } = formik;

    // 2.2 Effects

    // 2.3 Functions
    const handleClearFilters = async () => {
        setFieldValue('title', '');
        setFieldValue('body', '');
        await onFilter({ title: '', body: '' });
    };

    // 2.4 JSX Return
    return (
        <Box sx={{ borderRadius: 0 }}>
            <Accordion
                expanded={open}
                style={{ boxShadow: '0' }}
            >
                <AccordionSummary
                    onClick={() => setOpen(o => !o)}
                    expandIcon={
                        <Grid container display={"flex"} flexDirection={"row"}>
                            <Tooltip title={open ? "Hide Filters" : "Show Filters"}>
                                <ExpandMoreIcon/>
                            </Tooltip>
                        </Grid>
                    }
                >
                    <FilterListIcon sx={{ mr: 1.5, ml: -1, color: GT_COLORS.textMuted }} />
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: GT_COLORS.textPrimary }}>
                        Filters
                    </Typography>
                </AccordionSummary>
                
                <AccordionDetails sx={{ boxShadow: 0 }}>
                    <FormikProvider value={formik}>
                        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                            <Grid container spacing={1}>
                                <Grid item md={12}>
                                    <FormBuilder
                                        errors={errors}
                                        touched={touched}
                                        values={values}
                                        size="medium"
                                        inputs={MESSAGE_HISTORY_FILTER_INPUTS}
                                        setFieldValue={setFieldValue}
                                        getFieldProps={getFieldProps}
                                    />
                                </Grid>
                            </Grid>
                            
                            <Grid display="flex" flexDirection="row-reverse" mt={2}>
                                <LoadingButton
                                    variant="contained"
                                    loading={loading}
                                    type="submit"
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: 1,
                                        backgroundColor: GT_COLORS.primary,
                                        '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                                    }}
                                >
                                    Search
                                </LoadingButton>
                                <LoadingButton
                                    disabled={loading}
                                    variant="text"
                                    sx={{ mr: 1, textTransform: 'none', color: GT_COLORS.textMuted }}
                                    onClick={handleClearFilters}
                                >
                                    Clear filters
                                </LoadingButton>
                            </Grid>
                        </Form>
                    </FormikProvider>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};